import { useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Download, FileUp, Loader2, Upload } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import apiClient from '@/api/client'
import { describeApiError } from '@/lib/apiError'

const MAX_FILE_BYTES = 1 << 20 // mirrors the backend body cap (1 MiB)

type ImportResult = {
  mode: string
  created: number
  matched_updated: number
  skipped: Array<{ row: number; reason: string }>
  total_rows: number
}

/**
 * CSV guest-book import, three steps: pick file → dry-run preview → commit.
 *
 * The dry run IS the real loop server-side (rolled back), so the preview
 * counts are honest — including per-row skip reasons, downloadable as
 * errors.csv so the owner can fix their sheet and re-import. Matching is
 * merge-on-phone/email: re-importing the same file reports everything as
 * "matched", never duplicates.
 *
 * Column contract: name, phone (or phone_raw), email, birthday
 * (YYYY-MM-DD), whatsapp_opt_in — the segment export's own header works
 * as-is; unknown columns are ignored.
 */
export function ImportCustomersWizard({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const fileInput = useRef<HTMLInputElement | null>(null)
  const [fileName, setFileName] = useState('')
  const [csvText, setCsvText] = useState('')
  const [preview, setPreview] = useState<ImportResult | null>(null)
  const [committed, setCommitted] = useState<ImportResult | null>(null)

  const reset = () => {
    setFileName('')
    setCsvText('')
    setPreview(null)
    setCommitted(null)
    if (fileInput.current) fileInput.current.value = ''
  }

  const runImport = useMutation({
    mutationFn: (mode: 'dry_run' | 'commit') =>
      apiClient.importAdminCustomers({ csv_text: csvText, mode }),
    onSuccess: (res, mode) => {
      const data = res.data
      if (!res.success || !data) {
        toast({
          title: 'Import failed',
          description: res.message || 'Unknown error',
          variant: 'destructive',
        })
        return
      }
      if (mode === 'dry_run') {
        setPreview(data)
      } else {
        setCommitted(data)
        queryClient.invalidateQueries({ queryKey: ['customers'] })
        toast({
          title: 'Import complete',
          description: `${data.created} created, ${data.matched_updated} matched, ${data.skipped.length} skipped.`,
        })
      }
    },
    onError: (err: unknown) => {
      toast({
        title: 'Import failed',
        description: describeApiError(err).message,
        variant: 'destructive',
      })
    },
  })

  const onPickFile = (file: File | undefined) => {
    if (!file) return
    if (file.size > MAX_FILE_BYTES) {
      toast({
        title: 'File too large',
        description: 'Imports are capped at 1 MB (about 2,000 rows). Split the file and import in parts.',
        variant: 'destructive',
      })
      return
    }
    const reader = new FileReader()
    reader.onload = () => {
      setCsvText(String(reader.result ?? ''))
      setFileName(file.name)
      setPreview(null)
      setCommitted(null)
    }
    reader.readAsText(file)
  }

  const downloadErrors = (result: ImportResult) => {
    const lines = ['row,reason', ...result.skipped.map((s) => `${s.row},"${s.reason.replace(/"/g, '""')}"`)]
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'import-errors.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  const result = committed ?? preview

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o)
        if (!o) reset()
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" aria-hidden /> Import customers from CSV
          </DialogTitle>
          <DialogDescription>
            Bring your guest book from Excel or your old system. Columns:
            name, phone, email, birthday (YYYY-MM-DD), whatsapp_opt_in —
            extra columns are ignored. Existing customers are matched by
            phone/email and updated, never duplicated.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <input
            ref={fileInput}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => onPickFile(e.target.files?.[0])}
          />
          <Button
            variant="outline"
            className="w-full justify-start"
            onClick={() => fileInput.current?.click()}
            disabled={runImport.isPending}
          >
            <FileUp className="mr-2 h-4 w-4" aria-hidden />
            {fileName || 'Choose a .csv file…'}
          </Button>

          {result && (
            <div className="rounded-md border p-3 text-sm space-y-2">
              <p className="font-medium">
                {committed ? 'Imported' : 'Preview (nothing saved yet)'} —{' '}
                {result.total_rows} rows
              </p>
              <ul className="space-y-1 text-muted-foreground">
                <li>
                  {committed ? 'Created' : 'Will create'}: <strong>{result.created}</strong>
                </li>
                <li>
                  {committed ? 'Matched existing' : 'Will match existing'}:{' '}
                  <strong>{result.matched_updated}</strong>
                </li>
                <li>
                  Skipped: <strong>{result.skipped.length}</strong>
                </li>
              </ul>
              {result.skipped.length > 0 && (
                <>
                  <ul className="max-h-32 overflow-y-auto rounded bg-muted/50 p-2 text-xs space-y-0.5">
                    {result.skipped.slice(0, 20).map((s) => (
                      <li key={s.row}>
                        Row {s.row}: {s.reason}
                      </li>
                    ))}
                    {result.skipped.length > 20 && (
                      <li>…and {result.skipped.length - 20} more</li>
                    )}
                  </ul>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => downloadErrors(result)}
                    className="h-8 px-2"
                  >
                    <Download className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                    Download errors.csv
                  </Button>
                </>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={runImport.isPending}
          >
            {committed ? 'Done' : 'Cancel'}
          </Button>
          {!committed && !preview && (
            <Button
              onClick={() => runImport.mutate('dry_run')}
              disabled={!csvText || runImport.isPending}
            >
              {runImport.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
              )}
              Preview import
            </Button>
          )}
          {!committed && preview && (
            <Button
              onClick={() => runImport.mutate('commit')}
              disabled={runImport.isPending || preview.created + preview.matched_updated === 0}
            >
              {runImport.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
              )}
              Import {preview.created + preview.matched_updated} customers
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
